// src/lib/meetingContacts.js — the rows a meeting's contact pill shows (owner, 2026-09-06:
// "meetings must have a contact pill that will show all the contacts of involved people").
//
// The pill itself is the property card's ContactPill, unchanged in look and behaviour; this
// module only decides WHO is on a meeting. The rows it returns ride in front of the property's
// own contacts (owner, keyholder, …), which the pill still fetches for the meeting's property.
//
// Who counts as involved, per shape (MEETINGS_MODEL §2 / the state block's own reading):
//   • the CLIENT — a legacy scheduler row carries the person (name + phone). A core meeting
//     owns a relationship, not a contact, so it carries the client's REPRESENTATIVE instead;
//     the client's own number arrives only through the access request, never here.
//   • the PROPERTY REPRESENTATIVE (core rows) — the colleague you coordinate the flat with.
//   • the INVOLVED AGENTS — attendees with involved !== false on core rows, agent_ids on legacy
//     rows; numbers come from the scheduler roster (phone_work, then phone_personal).
// A person is listed once: a representative who is also an attendee is not repeated.
// Rows carry `is_agent` so ContactPill's ContactRow draws the agent badge and glyph it already
// has, rather than a role label invented for this surface.
import { t } from './i18n.js'

export function meetingContactRows(v, agents = []) {
  if (!v) return []
  const roster = id => (id ? agents.find(a => a.id === id) : null) || null
  const phoneOf = a => a?.phone_work || a?.phone_personal || null
  const rows = []
  const seenAgents = new Set()

  if (v.client_name || v.client_phone) {
    rows.push({ party_id: `meet-client:${v.id}`, name: v.client_name || null, phone: v.client_phone || null, role: 'client' })
  }
  const rep = (r, labelKey, key) => {
    if (!r?.name) return
    const a = roster(r.agent_id)
    if (r.agent_id) seenAgents.add(r.agent_id)
    // The server resolves the representative's phone (core_meeting.js snapshot); the roster is
    // the fallback for a colleague whose party has no number on record.
    // `core_party_id` lets the pill drop the SAME person from the property's contact list —
    // a representative is usually also the ad's agent, and was listed twice (owner, 2026-09-06).
    rows.push({ party_id: `meet-${key}:${v.id}`, core_party_id: r.party_id || null, name: `${r.name} · ${t(labelKey)}`, phone: r.phone || phoneOf(a), is_agent: true })
  }
  if (v.__core) {
    rep(v.client_representative, 'desk_meeting_row_client_rep', 'crep')
    rep(v.property_representative, 'desk_meeting_row_property_rep', 'prep')
  }
  const attendees = Array.isArray(v.attendees) ? v.attendees : []
  const agentIds = attendees.length
    ? attendees.filter(a => a.involved !== false).map(a => a.agent_id)
    : (v.agent_ids || [])
  for (const id of agentIds) {
    if (!id || seenAgents.has(id)) continue
    seenAgents.add(id)
    const a = roster(id)
    const name = a?.name || attendees.find(x => x.agent_id === id)?.name || null
    if (!name) continue
    rows.push({ party_id: `meet-agent:${id}`, name, phone: phoneOf(a), is_agent: true })
  }
  return rows
}

// The property the pill should also read contacts for — a core row's property_id, a legacy
// row's snapshot pointer. null when the meeting has no core property at all.
export function meetingPropertyId(v) {
  return v?.property_id || v?.property_snapshot?.core_property_id || null
}
